import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { qualityNcrAPI } from '@/lib/api';

const iso = (d) => d.toLocaleDateString('en-CA');
const yearAgo = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return iso(d); };
const num = (n, d = 0) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: d }));

const COLS = [
  ['complaints', 'Complaints'], ['open', 'Not closed'], ['repeat', 'Repeats (R)'], ['rejected', 'Rejected qty'],
  ['dispatched', 'Dispatched qty'], ['ppm', 'Customer PPM'], ['avg_closure_days', 'Avg days to corrective'],
  ['on_time_pct', 'Containment on time'], ['top_reason', 'Top reason'],
];

const Cell = ({ k, r }) => {
  if (k === 'ppm') return <span className={r.ppm ? 'text-red-600 font-semibold' : ''}>{num(r.ppm)}</span>;
  if (k === 'avg_closure_days') return <span className={r.avg_closure_days > 30 ? 'text-red-600' : ''}>{r.avg_closure_days === null ? '—' : `${r.avg_closure_days} d`}<span className="text-muted-foreground">{r.closed_with_action ? ` (${r.closed_with_action})` : ''}</span></span>;
  if (k === 'on_time_pct') return <span className={r.on_time_pct !== null && r.on_time_pct < 100 ? 'text-orange-600' : ''}>{r.on_time_pct === null ? '—' : `${r.on_time_pct}%`}</span>;
  if (k === 'top_reason') return <span title={r.top_reason}>{r.top_reason ? `${r.top_reason.slice(0, 32)}${r.top_reason.length > 32 ? '…' : ''} (${r.top_reason_count})` : '—'}</span>;
  if (k === 'repeat') return <span className={r.repeat ? 'text-red-600 font-semibold' : ''}>{r.repeat}</span>;
  return num(r[k], 2);
};

export default function CustomerScorecardPage() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(yearAgo());
  const [to, setTo] = useState(iso(new Date()));
  const [range, setRange] = useState({ from: yearAgo(), to: iso(new Date()) });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await qualityNcrAPI.scorecard({ date_from: range.from, date_to: range.to })).data.rows); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load scorecard'); } finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);
  const total = rows.reduce((s, r) => s + r.complaints, 0);
  return (
    <div className="space-y-4" data-testid="customer-scorecard-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight">Customer Complaint Scorecard</h1>
        <p className="text-xs text-muted-foreground">Per customer: complaints, customer PPM (complaint qty vs dispatched, imported old rows excluded), average days from complaint to corrective action done, and 7-day containment on-time rate.</p>
      </div>
      <Card className="shadow-none"><CardContent className="p-3 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="scorecard-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="scorecard-to" /></div>
        <Button size="sm" className="h-9 bg-orange-500 hover:bg-orange-600" onClick={() => (from > to ? toast.error('From date must be before To date') : setRange({ from, to }))} data-testid="scorecard-show-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}Show</Button>
        <span className="text-xs text-muted-foreground ml-auto" data-testid="scorecard-total">{rows.length} customers · {total} complaints</span>
      </CardContent></Card>
      <Card className="shadow-none"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs" data-testid="scorecard-table">
          <thead className="bg-muted/50 text-muted-foreground"><tr>
            <th className="text-left px-3 py-2 font-medium">Customer</th>
            {COLS.map(([k, l]) => <th key={k} className={`px-3 py-2 font-medium ${k === 'top_reason' ? 'text-left' : 'text-right'}`}>{l}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-muted-foreground">No complaints or dispatches in this period</td></tr>}
            {rows.map((r) => (
              <tr key={r.customer_key} className="border-t hover:bg-muted/40 cursor-pointer" data-testid={`scorecard-row-${r.customer_key}`}
                onClick={() => navigate(`/quality/complaints?customer=${encodeURIComponent(r.customer_name)}`)}>
                <td className="px-3 py-2 text-blue-700 whitespace-pre-line">{r.customer_name}{r.legacy ? <span className="ml-1 text-[10px] text-muted-foreground">({r.legacy} old)</span> : null}</td>
                {COLS.map(([k]) => <td key={k} className={`px-3 py-2 font-mono ${k === 'top_reason' ? 'text-left font-sans' : 'text-right'}`}><Cell k={k} r={r} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
